/* Client for the school-trip endpoints on the PEAR Render backend (Spark-safe).
   The browser never creates Firebase Auth accounts directly — it asks this
   backend, which holds the admin SDK. Base URL is the shared REACT_APP_PAYMENTS_URL. */
import { auth } from '../firebase';

const BASE = (process.env.REACT_APP_PAYMENTS_URL || '').replace(/\/$/, '');

export function isTripsApiConfigured(): boolean {
  return !!BASE;
}

async function authedPost<T>(path: string, body: unknown): Promise<T> {
  if (!BASE) throw new Error('The trips backend is not configured (REACT_APP_PAYMENTS_URL).');
  const token = await auth.currentUser?.getIdToken();
  if (!token) throw new Error('You appear to be signed out — please sign in again.');
  const res = await fetch(`${BASE}${path}`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
    body: JSON.stringify(body),
  });
  const json = await res.json().catch(() => ({}));
  if (!res.ok || json.ok === false) {
    // The HTTP status rides along so a caller can tell a refusal (403: the
    // server said no, and said why) from a failure worth falling back on
    // (2026-09-15, strict org gate on the trip routes).
    throw Object.assign(new Error(json.error || json.message || 'Request failed'), { status: res.status });
  }
  return json as T;
}

/** The hand-over credentials the backend returns after minting a temp conductor. */
export interface ConductorCreated {
  uid: string;
  username: string;
  password: string;
  displayName: string;
  /** Public one-time reveal link (also emailed / SMS’d). */
  revealUrl?: string;
  revealToken?: string;
  /** Fleet: delivery notes after emailing the permanent bus driver(s). */
  handoff?: {
    emailed?: number;
    drivers?: number;
    notes?: string[];
  };
  sms?: SmsDelivery;
}

export interface ConductorInput {
  username: string;
  password: string;
  displayName: string;
  /** Hired buses: phone for SMS one-time reveal link. */
  phone?: string;
  accessFromMillis?: number;
  accessUntilMillis?: number;
}

export interface HireBusArgs {
  orgCode: string;
  tripId: string;
  nickname: string;
  numberPlate?: string;
  expiresAtMillis: number;
  moves: { sourceBusId: string; childId: string }[];
  conductor?: ConductorInput;
}

export interface SmsDelivery {
  ok?: boolean;
  skipped?: boolean;
  error?: string;
  recipient?: string;
}

export function createHireBus(args: HireBusArgs): Promise<{ busId: string; conductor: ConductorCreated | null; sms?: SmsDelivery }> {
  return authedPost('/api/trip/hire-bus', args);
}

export function addTripConductor(args: { orgCode: string; tripId: string; busId: string; conductor: ConductorInput }): Promise<{ conductor: ConductorCreated }> {
  return authedPost('/api/trip/conductor', args);
}

export function notifyTripParents(args: { orgCode: string; tripId: string; parentIds: string[]; title: string; body: string }): Promise<{ sent: number; total: number }> {
  return authedPost('/api/trip/notify', args);
}

export function cancelTrip(args: { orgCode: string; tripId: string }): Promise<{ cancelled: boolean; conductorsDisabled: number; notified?: { sent: number; total: number } }> {
  return authedPost('/api/trip/cancel', args);
}

/** Expire a temp trip-conductor account (Accounts → Delete). */
export function disableTripConductor(args: { orgCode: string; uid?: string; username?: string }): Promise<{ disabled: boolean; reason?: string }> {
  return authedPost('/api/trip/conductor-disable', args);
}

/** Show-once credentials the backend returns after minting a temp parent, plus
 *  the outside children it created (each gets a generated childId). */
export interface TripParentCreated {
  uid: string;
  username: string;
  password: string;
  displayName: string;
  children: { childId: string; name: string }[];
  revealUrl?: string;
  revealToken?: string;
  sms?: SmsDelivery;
}

export interface OutsideChildInput {
  name: string;
  classLabel?: string;
}

export interface TripParentArgs {
  orgCode: string;
  tripId: string;
  /** The bus (fleet or hired) the outside children ride on this trip. */
  busId: string;
  displayName: string;
  /** Phone for SMS one-time reveal + credentials. */
  phone?: string;
  password?: string;
  accessFromMillis?: number;
  accessUntilMillis?: number;
  children: OutsideChildInput[];
}

/** Create a temporary parent account + their "outside children" (kids not in the
 *  fleet), attach them to the trip bus, and enrol them on the trip. */
export function createTripParent(args: TripParentArgs): Promise<{ parent: TripParentCreated }> {
  return authedPost('/api/trip/parent', args);
}

/** Mint a new outside-parent password + one-time reveal (admin). SMS when phone on file / Brevo configured. */
export function reissueTripParent(args: { orgCode: string; tripId: string; uid?: string; username?: string; phone?: string }): Promise<{ parent: TripParentCreated }> {
  return authedPost('/api/trip/parent-reissue', args);
}

/** Mint a new conductor password + one-time reveal (admin). SMS when phone on file / Brevo configured. */
export function reissueTripConductor(args: { orgCode: string; tripId: string; uid?: string; username?: string; phone?: string }): Promise<{ conductor: ConductorCreated }> {
  return authedPost('/api/trip/conductor-reissue', args);
}
